import * as THREE from 'three'
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js'
import { getUploadedModel, uploadedModelUrl } from '../api/modelCatalog'
import { apiFetch, BASE_URL } from '../api/http'
import { sessionEpoch } from '../api/session'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { STATUS_STYLES } from '../api/topology'
import { NODE_MODEL_ASSETS } from './nodeModels'
import { nodeModelVariantIndex, builtinModelPath } from './modelLibrary'

export { nodeModelVariantIndex } from './modelLibrary'

// Blender-authored assets are grouped by node category. A node id selects one
// stable variant, so the same topology keeps the same equipment mix.
export const NODE_GLTF_MODELS = Object.fromEntries(
  Object.entries(NODE_MODEL_ASSETS).map(([category, assets]) => [
    category,
    assets.map((asset) => builtinModelPath(asset))
  ])
)

const templates = new Map()
const loader = new GLTFLoader()
export function clearPrivateTemplates() {
  for (const [key, entry] of templates) {
    if (entry.uploaded) { entry.waiters.length = 0; templates.delete(key) }
  }
}
const LED_INTENSITY = 0.95
const MATERIAL_TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'alphaMap',
  'clearcoatMap',
  'clearcoatNormalMap',
  'clearcoatRoughnessMap',
  'sheenColorMap',
  'sheenRoughnessMap',
  'transmissionMap',
  'thicknessMap'
]

const statusColor = (status) => (STATUS_STYLES[status] || STATUS_STYLES.normal).color

// 生产构建的 publicPath 是 /stn/、开发环境是 /。BASE_URL 由 webpack 在打包时
// 替换成字符串字面量，所以这里不能再用 typeof process 之类的运行时判断包一层，
// 否则浏览器里 process 不存在，模型会退化成从站点根目录请求而 404。
const PUBLIC_BASE = process.env.BASE_URL || '/'

function modelUrl(path) {
  return `${PUBLIC_BASE.replace(/\/?$/, '/')}${path.replace(/^\/+/, '')}`
}

function normalizeTemplate(scene) {
  scene.updateMatrixWorld(true)
  const bounds = new THREE.Box3().setFromObject(scene)
  if (bounds.isEmpty()) return scene
  const center = bounds.getCenter(new THREE.Vector3())
  scene.position.x -= center.x
  scene.position.z -= center.z
  scene.position.y -= bounds.min.y
  scene.updateMatrixWorld(true)
  return scene
}

function prepareTemplate(scene) {
  scene.traverse((child) => {
    if (!child.isMesh) return
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.filter(Boolean).forEach((material) => {
      MATERIAL_TEXTURE_KEYS.forEach((key) => {
        const texture = material[key]
        if (!texture?.isTexture) return
        // Cloned model instances intentionally share the immutable glTF texture.
        // Mark it so disposing one node cannot invalidate the cached template.
        texture.userData.shared = true
        texture.anisotropy = Math.max(texture.anisotropy || 1, 8)
      })
    })
  })
  return normalizeTemplate(scene)
}

function loadTemplate(assetName, path, uploaded = false) {
  const cached = templates.get(assetName)
  if (cached) return cached
  const url = uploaded ? path : modelUrl(path)
  const epoch = sessionEpoch()
  const entry = { url, uploaded, status: 'loading', root: null, waiters: [] }
  templates.set(assetName, entry)
  const loaded = (gltf) => {
    if (uploaded && epoch !== sessionEpoch()) return
    entry.root = prepareTemplate(gltf.scene)
    if (uploaded) {
      const size = new THREE.Box3().setFromObject(entry.root).getSize(new THREE.Vector3())
      const extent = Math.max(size.x, size.y, size.z)
      if (Number.isFinite(extent) && extent > 0) {
        const wrapper = new THREE.Group()
        wrapper.add(entry.root)
        wrapper.scale.setScalar(38 / extent)
        entry.root = wrapper
      }
    }
    entry.status = 'ready'
    entry.waiters.splice(0).forEach((waiter) => waiter())
  }
  const failed = (error) => {
    entry.status = 'failed'
    entry.waiters.length = 0
    console.warn(`[gltfNodeModels] GLB load failed, using procedural fallback: ${url}`, error)
  }
  if (uploaded) {
    apiFetch(url.slice(BASE_URL.length)).then(response => response.arrayBuffer())
      .then(buffer => { if (epoch !== sessionEpoch()) throw new Error('账号已切换'); return loader.parseAsync(buffer, '') })
      .then(loaded).catch(failed)
  } else loader.load(url, loaded, undefined, failed)
  return entry
}

function inferSurfaceProfile(name) {
  const value = String(name || '').toLowerCase()
  if (value.includes('led') || value.includes('indicator')) {
    return { led: true, envMapIntensity: 1.1 }
  }
  if (value.includes('screen') || value.includes('glass') || value.includes('solar')) return { envMapIntensity: 1.2 }
  if (value.includes('gold') || value.includes('metal') || value.includes('trim')) return { envMapIntensity: 1.25 }
  if (value.includes('carbon')) return { envMapIntensity: 0.9 }
  if (value.includes('rubber')) return { envMapIntensity: 0.45 }
  return { envMapIntensity: 0.92 }
}

function buildMaterial(source, status) {
  const profile = inferSurfaceProfile(source.name)
  const isIndicator = Boolean(profile.led)
  // Keep Blender's complete glTF PBR material. Rebuilding it as a generic
  // MeshStandardMaterial loses clearcoat, transmission, authored roughness,
  // texture transforms and per-model colours.
  const material = source.clone()
  material.userData = { ...source.userData }
  if ('envMapIntensity' in material) material.envMapIntensity = profile.envMapIntensity
  if (isIndicator && material.emissive) {
    material.emissive.set(statusColor(status))
    material.emissiveIntensity = LED_INTENSITY
    material.userData.isIndicator = true
  }
  material.name = source.name
  if (material.emissive?.getHex() !== 0) {
    material.userData.baseEmissive = material.emissive.getHex()
    material.userData.baseEmissiveIntensity = material.emissiveIntensity
  }
  return material
}

function instantiate(template, status) {
  const group = cloneSkeleton(template)
  const bySource = new Map()
  const materials = []
  let primary = null
  let indicator = null

  group.traverse((child) => {
    if (!child.isMesh) return
    child.geometry = child.geometry.clone()
    const sources = Array.isArray(child.material) ? child.material : [child.material]
    const built = sources.map((source) => {
      if (!bySource.has(source.uuid)) {
        const material = buildMaterial(source, status)
        bySource.set(source.uuid, material)
        materials.push(material)
        if (!indicator && material.userData.isIndicator) indicator = material
      }
      return bySource.get(source.uuid)
    })
    child.material = Array.isArray(child.material) ? built : built[0]
    if (!primary) primary = child
  })

  return { group, materials, indicator: indicator || materials[0], primary }
}

export function createGLTFNodeModel(category, status, onReady, seed, modelId) {
  const custom = getUploadedModel(modelId)
  if (custom && (custom.category === category || custom.category === 'generic')) {
    const entry = loadTemplate(`${custom.id}:${custom.sha256}`, uploadedModelUrl(custom.id, custom.sha256), true)
    if (entry.status === 'loading' && onReady && !entry.waiters.includes(onReady)) entry.waiters.push(onReady)
    return entry.status === 'ready' ? instantiate(entry.root, status) : null
  }
  const assets = NODE_MODEL_ASSETS[category]
  if (!assets?.length) return null
  const assetName = assets[nodeModelVariantIndex(category, seed, modelId)]
  const entry = loadTemplate(assetName, builtinModelPath(assetName))
  if (entry.status === 'loading') {
    if (onReady) entry.waiters.push(onReady)
    return null
  }
  if (entry.status !== 'ready') return null
  return instantiate(entry.root, status)
}

export function preloadGLTFModels() {
  Object.entries(NODE_GLTF_MODELS).forEach(([category, paths]) => {
    const assetName = NODE_MODEL_ASSETS[category][0]
    loadTemplate(assetName, paths[0])
  })
}

